import React, { useState, useEffect } from 'react';
import { ShoppingCart, X, Plus, Minus, CheckCircle } from 'lucide-react';
import Cartview from './Cartview';
import { db } from '../../../firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import './NutriShop.css';

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <X size={24} />
        </button>
        {children}
      </div>
    </div>
  );
};

const NutriShop = () => {
  const [supplements, setSupplements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const [filters, setFilters] = useState({
    categoria: 'todos',
    subcategoria: 'todas',
    marca: 'todas'
  });
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [showCart, setShowCart] = useState(false);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    const unsubscribeSupplements = onSnapshot(collection(db, 'suplementos'), (snapshot) => {
      const productsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSupplements(productsData);
      setLoading(false);
    });

    const unsubscribeCats = onSnapshot(collection(db, 'categorias'), (snapshot) => {
      setCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.nombre.localeCompare(b.nombre)));
    });

    const unsubscribeSubcats = onSnapshot(collection(db, 'subcategorias'), (snapshot) => {
      setSubcategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.nombre.localeCompare(b.nombre)));
    });

    const unsubscribeBrands = onSnapshot(collection(db, 'marcas'), (snapshot) => {
      setBrands(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.nombre.localeCompare(b.nombre)));
    });

    return () => {
      unsubscribeSupplements();
      unsubscribeCats();
      unsubscribeSubcats();
      unsubscribeBrands();
    };
  }, []);

  const filteredSupplements = supplements.filter(s => {
    const matchCat = filters.categoria === 'todos' || s.categoria === filters.categoria;
    const matchSub = filters.subcategoria === 'todas' || s.subcategoria === filters.subcategoria;
    const matchBrand = filters.marca === 'todas' || s.marca === filters.marca;

    // El estado oculto sigue existiendo en el admin como una posibilidad (aunque no lo implementamos aún)
    const isVisible = s.estado !== 'oculto';
    return matchCat && matchSub && matchBrand && isVisible;
  });

  const availableSubcategories = subcategories.filter(sub => sub.categoriaPadre === filters.categoria);

  const handleProductClick = (suplemento) => {
    setSelectedProduct(suplemento);
    setQuantity(1);
    setShowDetailModal(true);
  };

  const getPrice = (product) => {
    return product.precioOferta ? parseFloat(product.precioOferta) : parseFloat(product.precio);
  };

  const addToCart = () => {
    const isOutOfStock = selectedProduct.estado === 'agotado' || Number(selectedProduct.stock || 0) <= 0;
    if (!selectedProduct || isOutOfStock) return;

    const price = getPrice(selectedProduct);
    const existingItem = cart.find(item => item.id === selectedProduct.id);
    if (existingItem) {
      setCart(cart.map(item =>
        item.id === selectedProduct.id
          ? { ...item, quantity: item.quantity + quantity, totalPrice: (item.quantity + quantity) * price }
          : item
      ));
    } else {
      const newItem = {
        ...selectedProduct,
        precio: price,
        quantity,
        totalPrice: price * quantity
      };
      setCart([...cart, newItem]);
    }

    setShowDetailModal(false);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  return (
    <section className="nutrishop" id="nutrishop">
      <div className="suplementos-content">
        <div className="suplementos-header-flex">
          <div className="header-text">
            <h2 className="title-main">Nutri<span className="highlight">Shop</span></h2>
            <p className="subtitle">Potencia tus resultados con nuestra selección premium</p>
          </div>
          <button
            className="cart-trigger"
            onClick={() => setShowCart(true)}
          >
            <ShoppingCart size={24} />
            {cart.length > 0 && <span className="cart-badge">{cart.length}</span>}
          </button>
        </div>

        <div className="filters-container">
          <div className="filter-group">
            <label>Categoría</label>
            <select
              value={filters.categoria}
              onChange={(e) => setFilters({ ...filters, categoria: e.target.value, subcategoria: 'todas' })}
            >
              <option value="todos">Todas las categorías</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.nombre}>{cat.nombre}</option>
              ))}
            </select>
          </div>

          {filters.categoria !== 'todos' && availableSubcategories.length > 0 && (
            <div className="filter-group">
              <label>Subcategoría</label>
              <select
                value={filters.subcategoria}
                onChange={(e) => setFilters({ ...filters, subcategoria: e.target.value })}
              >
                <option value="todas">Todas las subcategorías</option>
                {availableSubcategories.map(sub => (
                  <option key={sub.id} value={sub.nombre}>{sub.nombre}</option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-group">
            <label>Marca</label>
            <select value={filters.marca} onChange={(e) => setFilters({ ...filters, marca: e.target.value })}>
              <option value="todas">Todas las marcas</option>
              {brands.map(b => (
                <option key={b.id} value={b.nombre}>{b.nombre}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading-state">
            <p>Cargando Suplementos...</p>
          </div>
        ) : (
          <div className="suplementos-grid">
            {filteredSupplements.map((item) => {
              const isOutOfStock = item.estado === 'agotado' || Number(item.stock || 0) <= 0;
              return (
                <div key={item.id} className={`item-card ${isOutOfStock ? 'out-of-stock' : ''}`} onClick={() => handleProductClick(item)}>
                  <div className="item-image-wrapper">
                    <img src={item.imagen} alt={item.nombre} />
                    {isOutOfStock && <span className="stock-badge">Agotado</span>}
                    {item.precioOferta && <span className="offer-badge">Oferta</span>}
                    <div className="item-overlay">
                      <span>Ver Detalle</span>
                    </div>
                  </div>
                  <div className="item-details">
                    <div className="item-brand">{item.marca} {item.sku && <span className="sku-label">({item.sku})</span>}</div>
                    <h3>{item.nombre}</h3>
                    <div className="price-container">
                      {item.precioOferta ? (
                        <>
                          <span className="old-price">S/. {item.precio}</span>
                          <span className="current-price">S/. {item.precioOferta}</span>
                        </>
                      ) : (
                        <span className="current-price">S/. {item.precio}</span>
                      )}
                    </div>
                    <button className="add-quick-btn" disabled={isOutOfStock}>
                      {isOutOfStock ? 'Sin Stock' : 'Más Información'}
                    </button>
                  </div>
                </div>
              );
            })}
            {filteredSupplements.length === 0 && (
              <p className="no-items">Próximamente más productos en esta categoría.</p>
            )}
          </div>
        )}

        <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)}>
          {selectedProduct && (
            <div className="product-detail-view">
              <div className="detail-grid">
                <div className="detail-image">
                  <img src={selectedProduct.imagen} alt={selectedProduct.nombre} />
                </div>
                <div className="detail-info">
                  <span className="detail-category">{selectedProduct.categoria} {selectedProduct.subcategoria && `> ${selectedProduct.subcategoria}`}</span>
                  <h2>{selectedProduct.nombre}</h2>
                  {selectedProduct.marca && <div className="detail-brand">{selectedProduct.marca}</div>}
                  <p className="detail-description">{selectedProduct.descripcion}</p>
                  <div className="price-container detail">
                    {selectedProduct.precioOferta ? (
                      <>
                        <span className="old-price">S/. {selectedProduct.precio}</span>
                        <span className="current-price highlight">S/. {selectedProduct.precioOferta}</span>
                      </>
                    ) : (
                      <span className="current-price">S/. {selectedProduct.precio}</span>
                    )}
                  </div>

                  <div className="detail-actions">
                    {selectedProduct.estado !== 'agotado' && Number(selectedProduct.stock || 0) > 0 ? (
                      <>
                        <div className="qty-picker">
                          <button onClick={() => setQuantity(Math.max(1, quantity - 1))}><Minus size={16} /></button>
                          <span>{quantity}</span>
                          <button onClick={() => setQuantity(quantity + 1)}><Plus size={16} /></button>
                        </div>
                        <button className="btn-add-cart" onClick={addToCart}>
                          Agregar al Carrito - S/. {(getPrice(selectedProduct) * quantity).toFixed(2)}
                        </button>
                      </>
                    ) : (
                      <div className="out-of-stock-msg">Este producto no está disponible actualmente.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal>

        <Modal isOpen={showCart} onClose={() => setShowCart(false)}>
          <Cartview
            cart={cart}
            setCart={setCart}
            onClose={() => setShowCart(false)}
          />
        </Modal>

        {showToast && (
          <div className="cart-toast" onClick={() => setShowCart(true)}>
            <CheckCircle size={18} />
            <span>Producto añadido al carrito</span>
            <button className="toast-btn">Ver Carrito</button>
          </div>
        )}
      </div>
    </section>
  );
};

export default NutriShop;